import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  ListItemSecondaryAction,
  Grid,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  Typography,
  Chip,
  Badge,
  Avatar,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Fab,
  Drawer,
  AppBar,
  Toolbar,
  Breadcrumbs,
  Link,
  Rating,
  LinearProgress,
  AlertTitle,
} from '@mui/material';
import { 
  ShoppingCart, 
  ProductionQuantityLimits, 
  Receipt, 
  Person, 
  Add,
  Remove,
  Search,
  FilterList,
  Sort,
  Favorite,
  FavoriteBorder,
  Star,
  StarBorder,
  LocalShipping,
  Payment,
  CheckCircle,
  Schedule,
  Visibility,
  Edit,
  Delete,
  Refresh,
  Download,
  Print,
  Share,
  Notifications,
  AccountCircle,
  Settings,
  Help,
  ContactSupport,
  History,
  TrendingUp,
  TrendingDown,
  ExpandMore,
  Close,
  ArrowBack,
  ArrowForward,
  FirstPage,
  LastPage,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  AddShoppingCart,
  RemoveShoppingCart,
  ShoppingBasket,
  Inventory,
  Assessment,
  Analytics,
  Timeline,
  CalendarToday,
  LocationOn,
  Phone,
  Email,
  Business,
  AttachMoney,
  CreditCard,
  AccountBalance,
  QrCode,
  ReceiptLong,
  Description,
  Category,
  Grading,
  Palette,
  Straighten,
  Scale,
  Speed,
  Security,
  Verified,
  Warning,
  Error,
  Info,
} from '@mui/icons-material';
import { format, parseISO, addDays, isAfter, isBefore } from 'date-fns';
import { Order, Product, NewOrder } from '../../api/customerPortalApi';
import { useGetOrdersQuery, useGetProductsQuery, useCreateOrderMutation, useGetOrderDetailsQuery, useUpdateOrderStatusMutation } from '../../api/customerPortalApi';
import { useLoading } from '../../context/LoadingContext';

interface CustomerProfile {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  company: string;
  totalOrders: number;
  totalSpent: number;
  memberSince: string;
  loyaltyPoints: number;
  tier: 'bronze' | 'silver' | 'gold' | 'platinum';
  preferences: {
    notifications: boolean;
    marketing: boolean;
    language: string;
    currency: string;
  };
}

interface OrderItem {
  id: string;
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  status: 'pending' | 'confirmed' | 'in_production' | 'ready' | 'shipped' | 'delivered' | 'cancelled';
  estimatedDelivery: string;
  actualDelivery?: string;
  trackingNumber?: string;
  notes?: string;
}

interface CartItem {
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  customization?: any;
}

export const CustomerPortal: React.FC = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [newOrder, setNewOrder] = useState<NewOrder>({ items: [] });
  const [openOrderDialog, setOpenOrderDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showLoader, hideLoader } = useLoading();
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState({
    category: 'all',
    priceRange: 'all',
    availability: 'all'
  });
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [profile, setProfile] = useState<CustomerProfile>({
    id: '1',
    name: 'John Doe',
    email: 'john.doe@example.com',
    phone: '+254 700 000 000',
    address: 'Nairobi, Kenya',
    company: 'ABC Company',
    totalOrders: 15,
    totalSpent: 25000,
    memberSince: '2023-01-15',
    loyaltyPoints: 1250,
    tier: 'gold',
    preferences: {
      notifications: true,
      marketing: false,
      language: 'en',
      currency: 'KES'
    }
  });

  const { data: ordersData, isLoading: isOrdersLoading, error: ordersError, refetch: refetchOrders } = useGetOrdersQuery();
  const { data: productsData, isLoading: isProductsLoading, error: productsError } = useGetProductsQuery();
  const [createOrder] = useCreateOrderMutation();

  useEffect(() => {
    if (ordersError) {
      setError('Failed to fetch orders');
    }
  }, [ordersError]);

  useEffect(() => {
    if (productsError) {
      setError('Failed to fetch products');
    }
  }, [productsError]);

  const handleCreateOrder = async () => {
    try {
      setLoading(true);
      setError(null);

      await createOrder(newOrder);
      setOpenOrderDialog(false);
      setNewOrder({ items: [] });
      setCart([]);
      refetchOrders();
    } catch (err) {
      setError('Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product: Product, quantity: number = 1) => {
    const existingItem = cart.find(item => item.productId === product.id);
    const unitPrice = product.price ?? 0;
    
    if (existingItem) {
      setCart(prev => prev.map(item => 
        item.productId === product.id 
          ? { ...item, quantity: item.quantity + quantity, totalPrice: (item.quantity + quantity) * item.unitPrice }
          : item
      ));
    } else {
      setCart(prev => [...prev, {
        productId: product.id,
        productName: product.name,
        quantity,
        unitPrice,
        totalPrice: quantity * unitPrice,
        customization: {}
      }]);
    }
  };

  const removeFromCart = (productId: string) => {
    setCart(prev => prev.filter(item => item.productId !== productId));
  };

  const updateCartQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    setCart(prev => prev.map(item => 
      item.productId === productId 
        ? { ...item, quantity, totalPrice: quantity * item.unitPrice }
        : item
    ));
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + item.totalPrice, 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const filteredProducts = productsData?.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = filter.category === 'all' || product.category === filter.category;
    const matchesAvailability = filter.availability === 'all' || 
                               (filter.availability === 'in_stock' && product.inStock) ||
                               (filter.availability === 'out_of_stock' && !product.inStock);
    
    return matchesSearch && matchesCategory && matchesAvailability;
  }) || [];

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    let aValue = a[sortBy as keyof Product];
    let bValue = b[sortBy as keyof Product];
    
    if (typeof aValue === 'string') aValue = aValue.toLowerCase();
    if (typeof bValue === 'string') bValue = bValue.toLowerCase();
    
    if ((aValue as any) < (bValue as any)) return sortOrder === 'asc' ? -1 : 1;
    if ((aValue as any) > (bValue as any)) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const renderOrderStatus = (status: string) => {
    const statusConfig = {
      pending: { color: 'warning', icon: <Schedule />, label: 'Pending' },
      confirmed: { color: 'info', icon: <CheckCircle />, label: 'Confirmed' },
      in_production: { color: 'primary', icon: <ProductionQuantityLimits />, label: 'In Production' },
      ready: { color: 'success', icon: <CheckCircle />, label: 'Ready' },
      shipped: { color: 'info', icon: <LocalShipping />, label: 'Shipped' },
      delivered: { color: 'success', icon: <CheckCircle />, label: 'Delivered' },
      cancelled: { color: 'error', icon: <Close />, label: 'Cancelled' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color as any}
        size="small"
      />
    );
  };

  const renderTierBadge = (tier: string) => {
    const tierConfig = {
      bronze: { color: '#cd7f32', label: 'Bronze' },
      silver: { color: '#c0c0c0', label: 'Silver' },
      gold: { color: '#ffd700', label: 'Gold' },
      platinum: { color: '#e5e4e2', label: 'Platinum' }
    };

    const config = tierConfig[tier as keyof typeof tierConfig] || tierConfig.bronze;
    
    return (
      <Chip
        label={config.label}
        sx={{ 
          backgroundColor: config.color, 
          color: 'white',
          fontWeight: 'bold'
        }}
        size="small"
      />
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Breadcrumbs */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Breadcrumbs>
          <Link color="inherit" href="#" underline="hover">
            Home
          </Link>
          <Typography color="text.primary">
            {tabIndex === 0 ? 'Orders' : tabIndex === 1 ? 'Products' : 'Create Order'}
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Profile Summary */}
      <Paper sx={{ m: 2, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Avatar sx={{ width: 56, height: 56 }}>
              <Person />
            </Avatar>
          </Grid>
          <Grid item xs>
            <Typography variant="h6">{profile.name}</Typography>
            <Typography variant="body2" color="textSecondary">
              {profile.company} • Member since {format(parseISO(profile.memberSince), 'MMM yyyy')}
            </Typography>
          </Grid>
          <Grid item>
            {renderTierBadge(profile.tier)}
          </Grid>
          <Grid item>
            <Typography variant="h6" color="primary.main">
              {profile.loyaltyPoints} pts
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Loyalty Points
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="h6" color="success.main">
              ${profile.totalSpent.toLocaleString()}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Total Spent
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mx: 2, mb: 2 }}>
        <Tabs
          value={tabIndex}
          onChange={(_, newValue) => setTabIndex(newValue)}
          sx={{ mb: 3 }}
        >
          <Tab label="My Orders" />
          <Tab label="Products" />
          <Tab label="Create Order" />
          <Tab label="Profile" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', px: 2, pb: 2 }}>
          {tabIndex === 0 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">My Orders</Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setTabIndex(2)}
                >
                  Create New Order
                </Button>
              </Box>
              
              {isOrdersLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : ordersData?.length ? (
                <Grid container spacing={2}>
                  {ordersData.map((order: Order) => (
                    <Grid item xs={12} md={6} key={order.id}>
                      <Card>
                        <CardHeader
                          title={`Order #${order.orderNumber}`}
                          subheader={order.createdAt ? format(parseISO(order.createdAt), 'MMM d, yyyy') : 'Date unavailable'}
                          action={renderOrderStatus(order.status)}
                        />
                        <CardContent>
                          <Typography variant="h6" color="primary.main" gutterBottom>
                            ${(order.totalAmount ?? 0).toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            {order.items?.length ?? 0} items
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Visibility />}
                              onClick={() => setSelectedOrder(order)}
                            >
                              View Details
                            </Button>
                            {order.status === 'pending' && (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Edit />}
                                color="warning"
                              >
                                Modify
                              </Button>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="h6" color="textSecondary" gutterBottom>
                    No orders found
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Start by creating your first order
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setTabIndex(2)}
                    sx={{ mt: 1 }}
                  >
                    Create Order
                  </Button>
                </Paper>
              )}
            </Box>
          )}

          {tabIndex === 1 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Products</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={filter.category}
                      onChange={(e) => setFilter(prev => ({ ...prev, category: e.target.value }))}
                      label="Category"
                    >
                      <MenuItem value="all">All Categories</MenuItem>
                      <MenuItem value="electronics">Electronics</MenuItem>
                      <MenuItem value="furniture">Furniture</MenuItem>
                      <MenuItem value="clothing">Clothing</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {isProductsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {sortedProducts.map((product: Product) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardMedia
                          component="img"
                          height="200"
                          image={typeof product.image === 'string' ? product.image : 'https://via.placeholder.com/300x200'}
                          alt={product.name}
                        />
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" gutterBottom>
                            {product.name}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            {product.description}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Rating value={Number(product.rating) || 0} readOnly size="small" />
                            <Typography variant="caption" color="textSecondary">
                              ({Number(product.reviews) || 0})
                            </Typography>
                          </Box>
                          <Typography variant="h6" color="primary.main" gutterBottom>
                            ${(product.price ?? 0).toLocaleString()}
                          </Typography>
                          <Chip
                            label={product.inStock ? 'In Stock' : 'Out of Stock'}
                            color={product.inStock ? 'success' : 'error'}
                            size="small"
                            sx={{ mb: 1 }}
                          />
                        </CardContent>
                        <Box sx={{ p: 2, pt: 0 }}>
                          <Button
                            variant="contained"
                            fullWidth
                            startIcon={<AddShoppingCart />}
                            onClick={() => addToCart(product)}
                            disabled={!product.inStock}
                          >
                            Add to Cart
                          </Button>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          {tabIndex === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Create New Order
              </Typography>
              
              {cart.length > 0 ? (
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Shopping Cart ({getCartItemCount()} items)
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Product</TableCell>
                          <TableCell>Price</TableCell>
                          <TableCell>Quantity</TableCell>
                          <TableCell>Total</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cart.map((item) => (
                          <TableRow key={item.productId}>
                            <TableCell>{item.productName}</TableCell>
                            <TableCell>${item.unitPrice.toLocaleString()}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => updateCartQuantity(item.productId, item.quantity - 1)}
                                >
                                  <Remove />
                                </IconButton>
                                <Typography>{item.quantity}</Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => updateCartQuantity(item.productId, item.quantity + 1)}
                                >
                                  <Add />
                                </IconButton>
                              </Box>
                            </TableCell>
                            <TableCell>${item.totalPrice.toLocaleString()}</TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => removeFromCart(item.productId)}
                                color="error"
                              >
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <Typography variant="h6">
                      Total: ${getCartTotal().toLocaleString()}
                    </Typography>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => setOpenOrderDialog(true)}
                      disabled={cart.length === 0}
                    >
                      Place Order
                    </Button>
                  </Box>
                </Paper>
              ) : (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="h6" color="textSecondary" gutterBottom>
                    Your cart is empty
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Browse our products and add items to your cart
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<ShoppingCart />}
                    onClick={() => setTabIndex(1)}
                    sx={{ mt: 1 }}
                  >
                    Browse Products
                  </Button>
                </Paper>
              )}
            </Box>
          )}

          {tabIndex === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Profile & Settings
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardHeader title="Personal Information" />
                    <CardContent>
                      <List>
                        <ListItem>
                          <ListItemText
                            primary="Name"
                            secondary={profile.name}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Email"
                            secondary={profile.email}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Phone"
                            secondary={profile.phone}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Address"
                            secondary={profile.address}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Company"
                            secondary={profile.company}
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardHeader title="Account Statistics" />
                    <CardContent>
                      <List>
                        <ListItem>
                          <ListItemText
                            primary="Total Orders"
                            secondary={profile.totalOrders}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Total Spent"
                            secondary={`$${profile.totalSpent.toLocaleString()}`}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Loyalty Points"
                            secondary={profile.loyaltyPoints}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Member Since"
                            secondary={format(parseISO(profile.memberSince), 'MMM dd, yyyy')}
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>

      {/* Shopping Cart Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{ '& .MuiDrawer-paper': { width: 400 } }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Shopping Cart</Typography>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <Close />
            </IconButton>
          </Box>
          
          {cart.length > 0 ? (
            <>
              <List>
                {cart.map((item) => (
                  <ListItem key={item.productId}>
                    <ListItemText
                      primary={item.productName}
                      secondary={`$${item.unitPrice.toLocaleString()} x ${item.quantity}`}
                    />
                    <ListItemSecondaryAction>
                      <Typography variant="body2" fontWeight="bold">
                        ${item.totalPrice.toLocaleString()}
                      </Typography>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Total</Typography>
                <Typography variant="h6" color="primary.main">
                  ${getCartTotal().toLocaleString()}
                </Typography>
              </Box>
              <Button
                variant="contained"
                fullWidth
                onClick={() => {
                  setDrawerOpen(false);
                  setTabIndex(2);
                }}
              >
                Checkout
              </Button>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ShoppingCart sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                Your cart is empty
              </Typography>
              <Button
                variant="outlined"
                onClick={() => {
                  setDrawerOpen(false);
                  setTabIndex(1);
                }}
              >
                Browse Products
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Order Details Dialog */}
      <Dialog open={!!selectedOrder} onClose={() => setSelectedOrder(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Order Details - #{selectedOrder?.orderNumber}
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  {renderOrderStatus(selectedOrder.status)}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Total Amount</Typography>
                  <Typography variant="h6" color="primary.main">
                    ${(selectedOrder.totalAmount ?? 0).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Created</Typography>
                  <Typography variant="body2">
                    {selectedOrder.createdAt ? format(parseISO(selectedOrder.createdAt), 'MMM dd, yyyy HH:mm') : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Items</Typography>
                  <Typography variant="body2">{selectedOrder.items?.length ?? 0} items</Typography>
                </Grid>
              </Grid>
              
              <Typography variant="h6" gutterBottom>
                Order Items
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Price</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {((selectedOrder.items ?? []) as Array<{ id: string; type: string; quantity: number; status: string }>).map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.type}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>{item.status}</TableCell>
                        <TableCell>${item.quantity * 1000}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedOrder(null)}>Close</Button>
          <Button variant="contained" startIcon={<Print />}>
            Print Receipt
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Order Dialog */}
      <Dialog open={openOrderDialog} onClose={() => setOpenOrderDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Confirm Order</DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            Order Summary
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cart.map((item) => (
                  <TableRow key={item.productId}>
                    <TableCell>{item.productName}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell>${item.unitPrice.toLocaleString()}</TableCell>
                    <TableCell>${item.totalPrice.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 2, textAlign: 'right' }}>
            <Typography variant="h6">
              Total: ${getCartTotal().toLocaleString()}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenOrderDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCreateOrder}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Place Order'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ position: 'fixed', top: 16, right: 16, zIndex: 9999 }}>
          {error}
        </Alert>
      )}

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="create order"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => setTabIndex(2)}
      >
        <Add />
      </Fab>
    </Box>
  );
};
